import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { withUser } from 'store/UserProvider'
import { Link } from 'react-router-dom'
import DropZone from '@unowmooc/react-ui-kit/lib/DropZone'
import DropZoneContent from '@unowmooc/react-ui-kit/lib/DropZoneContent'
import I18nProvider from '@unowmooc/i18n'
import { Alert, Box, DataGrid } from '@unowmooc/react-ui-kit'
import AwesomeUnowLoader from 'components/ReactUiKit/AwesomeUnowLoader'
import { Col, Row } from 'react-grid-system'
import styled from '@emotion/styled'
import config from 'config'
import { mq } from '@unowmooc/themes'
import { css } from '@emotion/core'
import gql from 'graphql-tag'
import axios from 'utils/axios'
import DataGridCell from 'components/ReactUiKit/DataGrid/Cell'
import moment from 'moment'
import { useQuery } from 'react-apollo'
import { Flex } from 'reflexbox'
import { DataGridCellAction, DataGridCellActionLink } from 'components/ReactUiKit/DataGrid'
import styles from './styles.module.scss'

const StyledCol = styled(Col)`
  ${mq.md(css`
    &:nth-of-type(1),
    &:nth-of-type(2) {
      margin-bottom: 20px;
    }
  `)};

  ${mq.sm(css`
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  `)};
`
const UPLOAD_TIMEOUT_MILISECONDS = 240000

const QUERY_VIRTUAL_CLASSROOM = gql`
  query VirtualClassroomView($vcId: ID!) {
    virtualClassroom(id: $vcId) {
      id
      isOnSite
      sequenceNumber
      startDate
      endDate
      files {
        id
        isDeletable
        file {
          id
          filename
        }
        createdAt
      }
    }
  }
`

const FILE_STATUSES = {
  FILE_STATUS_READY: 'FILE_STATUS_READY',
  FILE_STATUS_LOADING: 'FILE_STATUS_LOADING',
  FILE_STATUS_ERROR: 'FILE_STATUS_ERROR',
  FILE_STATUS_SUCCESS: 'FILE_STATUS_SUCCESS',
}

const DataGridCellActionLinkDelete = props => {
  const deleteItem = () => {
    axios.post(`/virtual-classroom-file/delete?id=${props.data.id}`).then(() => {
      props.onEnded()
    })
  }

  return <DataGridCellActionLink onClick={deleteItem}>Supprimer</DataGridCellActionLink>
}

DataGridCellActionLinkDelete.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  onEnded: PropTypes.func.isRequired,
}

const ManageSignInSheets = ({
  match: {
    params: { idVirtualClassroom },
  },
}) => {
  const { data: dataVirtualClassroom, loading: loadingVirtualClassroom, refetch } = useQuery(QUERY_VIRTUAL_CLASSROOM, {
    variables: { vcId: idVirtualClassroom },
    fetchPolicy: 'network-only',
  })
  const [fileStatus, setFileStatus] = useState(FILE_STATUSES.FILE_STATUS_READY)
  const [fileMessage, setFileMessage] = useState(null)

  const resetFileState = () => {
    setFileStatus(FILE_STATUSES.FILE_STATUS_READY)
    setFileMessage(null)
  }

  const doUploadFile = files => {
    setFileStatus(FILE_STATUSES.FILE_STATUS_LOADING)

    const formData = new FormData()
    const file = files[0]
    formData.append('file', file)

    const uploadConfig = {
      timeout: UPLOAD_TIMEOUT_MILISECONDS,
    }

    axios
      .post(`/virtual-classroom-file/upload?virtualClassroomId=${idVirtualClassroom}`, formData, uploadConfig)
      .then(() => {
        refetch().then(() => {
          setFileStatus(FILE_STATUSES.FILE_STATUS_SUCCESS)
        })
      })
      .catch(returnValue => {
        setFileStatus(FILE_STATUSES.FILE_STATUS_ERROR)
        setFileMessage(returnValue.response.data.error.displayable_message)
      })
  }

  if (loadingVirtualClassroom) {
    return (
      <Flex justifyContent="center">
        <AwesomeUnowLoader />
      </Flex>
    )
  }

  const { virtualClassroom } = dataVirtualClassroom

  return (
    <>
      <h2 style={{ marginBottom: '30px' }}>
        Émargement de l&apos;
        <FormattedMessage
          id={
            virtualClassroom.isOnSite
              ? 'virtual_classroom.label_cms_block_is_on_site'
              : 'virtual_classroom.label_cms_block'
          }
          values={{ number: virtualClassroom.sequenceNumber }}
        />{' '}
        du {moment(virtualClassroom.startDate).format('DD/MM/YYYY')} de{' '}
        {moment(virtualClassroom.startDate).format('hh:mm')} à {moment(virtualClassroom.endDate).format('hh:mm')}
      </h2>

      <Row style={{ marginBottom: '30px' }}>
        <StyledCol xs={12} sm={12} md={12}>
          <Link
            to={I18nProvider.getLinkRoute('/expert/virtual-classrooms/view/{idVirtualClassroom}', {
              idVirtualClassroom,
            })}
          >
            <FormattedMessage id="virtual_classroom_files.back_to_virtual_classroom_page" />
          </Link>
        </StyledCol>
      </Row>

      <Row style={{ marginBottom: '30px' }}>
        <StyledCol xs={12} sm={12} md={12}>
          <Box>
            <h2 style={{ marginBottom: '20px' }}>Ajouter un fichier </h2>
            {fileStatus === FILE_STATUSES.FILE_STATUS_LOADING && (
              <div style={{ textAlign: 'center' }}>
                <AwesomeUnowLoader />
              </div>
            )}
            {fileStatus === FILE_STATUSES.FILE_STATUS_ERROR && (
              <Alert theme="error" style={{ textAlign: 'center' }}>
                {fileMessage}.
                <div style={{ marginTop: '10px' }}>
                  <a href="#" onClick={resetFileState}>
                    <FormattedMessage id="virtual_classroom_files.add_again" />
                  </a>
                </div>
              </Alert>
            )}
            {fileStatus === FILE_STATUSES.FILE_STATUS_SUCCESS && (
              <Alert style={{ textAlign: 'center' }}>
                Fichier envoyé.
                <div style={{ marginTop: '10px' }}>
                  <a href="#" onClick={resetFileState}>
                    <FormattedMessage id="virtual_classroom_files.add" />
                  </a>
                </div>
              </Alert>
            )}
            {fileStatus === FILE_STATUSES.FILE_STATUS_READY && (
              <DropZone
                onDrop={doUploadFile}
                render={({ active, openDialog }) => (
                  <DropZoneContent
                    active={active}
                    openDialog={openDialog}
                    addLabel="Ajouter"
                    dropLabel="Déplacer"
                    allowedFormats={['JPG', 'PNG', 'PDF']}
                    allowedFormatsLabel="Formats acceptés"
                  />
                )}
              />
            )}
          </Box>
        </StyledCol>
      </Row>

      <Row style={{ marginBottom: '30px' }}>
        <StyledCol xs={12} sm={12} md={12}>
          <Box>
            <div className={styles.dataGridWithoutShadow}>
              <h2>
                <FormattedMessage id="virtual_classroom_files.sent_files" />
              </h2>

              <DataGrid
                data={virtualClassroom.files}
                emptyStateMessage={<FormattedMessage id="virtual_classroom_files.no_file_sent" />}
              >
                <DataGridCell
                  label={<FormattedMessage id="virtual_classroom_files.list.filename" />}
                  render={vcFile => (
                    <>
                      <a
                        target="vc-file-download"
                        href={`${config.env.GRAPHQL_API_URL}/virtual-classroom-file/download?id=${vcFile.id}`}
                      >
                        {vcFile.file.filename}
                      </a>
                    </>
                  )}
                />
                <DataGridCell
                  label={<FormattedMessage id="virtual_classroom_files.list.created_at" />}
                  property="createdAt"
                  render={resultCell => moment(resultCell).format('DD/MM/YYYY')}
                />
                <DataGridCellAction>
                  <DataGridCellActionLinkDelete onEnded={refetch} shouldBeDisplayed={item => item.isDeletable} />
                </DataGridCellAction>
              </DataGrid>
            </div>
          </Box>
        </StyledCol>
      </Row>

      {/* Pour le moment on ne gère pas les commentaires, ça sera à faire dans une future version */}
      {/* <h2>Commentaire à propos de l'émargement</h2> */}
      {/* Pas de commentaire saisi. */}
      {/* Saisir un commentzaire. */}
    </>
  )
}

ManageSignInSheets.propTypes = {
  match: PropTypes.shape().isRequired,
}

export default withUser(ManageSignInSheets)
